import React from 'react';

import { useSelector } from 'react-redux';

import Loader from 'components/Loader';

import {
  GetStoryDetailsDocument,
  PlaylistTypeEnum,
  useAddStoryToPlaylistMutation,
  useRemoveStoryFromPlaylistMutation,
  useUserPlaylistsQuery,
} from 'graphql/generated/graphql';

import useGraphaelDefaults from 'hooks/useGraphaelDefaults';

import { getStoryIdToBeAdded } from 'store/modules/playlist';

import ListItem, { useCloseOverlayPageAndShowBanner } from '../ListItem';

const Playlists = () => {
  const graphaelDefaults = useGraphaelDefaults();

  const { data, loading } = useUserPlaylistsQuery({
    ...graphaelDefaults,
    fetchPolicy: 'cache-and-network',
    variables: {
      types: [PlaylistTypeEnum.User],
    },
  });

  const playlists = data?.playlists;
  const userPlaylists =
    playlists?.filter((playlist) => playlist.type === PlaylistTypeEnum.User) ||
    [];

  const storyIdToBeAdded = useSelector(getStoryIdToBeAdded);

  const closeOverlayPageAndShowBanner = useCloseOverlayPageAndShowBanner();

  const [addStoryToPlaylist] = useAddStoryToPlaylistMutation({
    ...graphaelDefaults,
    refetchQueries: [
      {
        query: GetStoryDetailsDocument,
        variables: {
          storyId: storyIdToBeAdded?.id,
        },
        context: graphaelDefaults.context,
      },
    ],
    onCompleted: (data) => {
      closeOverlayPageAndShowBanner('SM0123.PlaylistBanner.successmessage');
    },
  });

  const [removeStoryFromPlaylist] = useRemoveStoryFromPlaylistMutation({
    ...graphaelDefaults,
    refetchQueries: [
      {
        query: GetStoryDetailsDocument,
        variables: {
          storyId: storyIdToBeAdded?.id,
        },
        context: graphaelDefaults.context,
      },
    ],
    onCompleted: (data) => {
      closeOverlayPageAndShowBanner('SM0123.PlaylistBanner.successmessage2');
    },
  });

  const playlistIds = storyIdToBeAdded?.playlists.map(
    (playlist) => playlist.id,
  );

  const isStoryInPlaylists = (playlistId: string) => {
    return playlistIds && playlistIds.includes(playlistId);
  };

  const handleOnClick = (playlistId: string) => {
    if (!storyIdToBeAdded) return;

    if (!isStoryInPlaylists(playlistId)) {
      addStoryToPlaylist({
        variables: {
          input: {
            playlistId: playlistId,
            storyId: storyIdToBeAdded.id,
          },
        },
      });
    } else {
      removeStoryFromPlaylist({
        variables: {
          input: {
            playlistId: playlistId,
            storyId: storyIdToBeAdded.id,
          },
        },
      });
    }
  };

  return (
    <>
      {loading && userPlaylists.length < 1 && <Loader />}
      {userPlaylists
        .filter((playlist) => !!playlist.name)
        .map((playlist) => (
          <div key={playlist.id}>
            <ListItem
              selected={isStoryInPlaylists(playlist.id)}
              text={playlist.name!}
              onClick={() => {
                handleOnClick(playlist.id);
              }}
            />
          </div>
        ))}
    </>
  );
};

export default Playlists;
